import React, { useEffect, useRef } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { BoxProps } from '@osmosis/tailwind-ui/components/Box';
import { useLearnifyText } from '../../hooks/useLearnifyText';
import { useQuizContext } from '../../context/QuizContext';

type Props = BoxProps & {
  text: string;
  enabled?: boolean;
};

/**
 * LearnifyText.
 */
const LearnifyText = (props: Props) => {
  const { text, enabled = true, ...rest } = props;
  const { result: learnifiedText } = useLearnifyText(text, enabled);
  const { highlighting } = useQuizContext();
  const textRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = textRef.current;
    if (!node) return;

    // Prevent actual content edits while still allowing caret & selection
    const onBeforeInput = (e: InputEvent) => {
      e.preventDefault();
    };
    node.addEventListener('beforeinput', onBeforeInput);

    // Key handler: press 'h' to highlight current selection
    const onKeyDown = (e: KeyboardEvent) => {
      // Avoid triggering when focus is in an input/textarea elsewhere
      // and allow modifier combos (e.g., Ctrl+H should not trigger)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key.toLowerCase() === 'h') {
        // Prevent default so typed 'h' doesn't appear in contenteditable
        e.preventDefault();

        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
          announce('No text selected.');
          return;
        }

        const range = sel.getRangeAt(0);

        // Sanity: don't allow cross-container selections (must be inside node)
        if (!node.contains(range.commonAncestorContainer)) {
          announce('Selection not inside the text area.');
          return;
        }

        try {
          // Create a <mark> and move the selected contents into it.
          const mark = document.createElement('mark');
          mark.className = 'a11y-highlight';

          // Use surroundContents if selection boundaries are valid
          // Surround may throw if selection partially selects non-text nodes.
          // Fallback to manual extract if needed.
          range.surroundContents(mark);

          // Collapse selection (so caret moves after the highlight)
          sel.removeAllRanges();
          // Move focus back so caret is visible
          node.focus();
          announce('Highlight added.');
        } catch (err) {
          // Fallback: extractContents + append into mark + insertNode
          // This handles more complex DOM boundary cases.
          try {
            const mark = document.createElement('mark');
            mark.className = 'a11y-highlight';
            const extracted = range.extractContents();
            mark.appendChild(extracted);
            range.insertNode(mark);
            sel.removeAllRanges();
            node.focus();
            announce('Highlight added.');
          } catch (err2) {
            // If both fail, let user know
            // eslint-disable-next-line no-console
            console.error('Failed to apply highlight:', err, err2);
            announce('Could not highlight selection.');
          }
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      node.removeEventListener('beforeinput', onBeforeInput);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [highlighting]);

  function announce(message: string): void {
    if (statusRef.current) statusRef.current.textContent = message;
  }

  return (
    <>
      <Box
        {...rest}
        style={{
          ...rest?.style,
        }}
        dangerouslySetInnerHTML={{
          __html: enabled ? learnifiedText : text,
        }}
        className={`dark:text-white [&_a]:text-blue-600 [&_a]:underline ${rest?.className}`}
        contentEditable={highlighting ? 'true' : 'false'}
        ref={textRef}
      />
      <div ref={statusRef} aria-live="polite" className="sr-only" />
    </>
  );
};

export default LearnifyText;
